import { useState, useEffect } from 'react';
import axios from 'axios';
import { Flame, Check, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOLD = '#D89232';
const GOLD_INK = '#8F650F';
const TERRA = '#E07A5F';

const fmtMinutes = (m) => {
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return rest > 0 ? `${h} Std ${rest} Min` : `${h} Std`;
  }
  return `${m} Min`;
};

export const StreakCard = ({ className = '' }) => {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [weekly, setWeekly] = useState(null);

  useEffect(() => {
    axios.get(`${API}/user/streak`, getAuthHeader())
      .then(r => setData(r.data))
      .catch(() => {});
    axios.get(`${API}/user/weekly-stats`, getAuthHeader())
      .then(r => setWeekly(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return null;

  const remaining = Math.max(0, data.weekly_goal_days - data.days_this_week);
  const days = weekly?.days || [];
  const maxMin = Math.max(1, ...days.map(d => d.minutes));
  const weekMin = weekly?.week_minutes || 0;
  const lastWeekMin = weekly?.last_week_minutes || 0;
  const delta = weekMin - lastWeekMin;

  const motivation = data.goal_reached
    ? 'Wochenziel erreicht — du bist deiner Zeit voraus.'
    : data.current_streak >= 3
      ? `${data.current_streak} Tage in Serie — Dranbleiben schlägt Talent.`
      : weekMin > 0
        ? 'Jede Minute zählt — schön, dass du da warst.'
        : 'Ein Kapitel heute — und die Woche gehört dir.';

  return (
    <div className={`bg-cream-100 border border-cream-200 rounded-3xl p-6 sm:p-7 shadow-soft ${className}`} data-testid="streak-card">
      {/* Kopfzeile */}
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-5">
        <span className="font-mono text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.24em', color: GOLD_INK }}>
          Deine Woche
        </span>
        <div className="flex items-baseline gap-2.5" data-testid="week-total">
          <span className="font-display text-[22px] leading-none text-espresso">{fmtMinutes(weekMin)}</span>
          <span className="text-[11px] text-espresso/45">gelernt</span>
          {lastWeekMin > 0 && delta !== 0 && (
            <span className="text-[11px] font-semibold" style={{ color: delta > 0 ? GOLD_INK : '#8A8175' }}>
              {delta > 0 ? '▲' : '▽'} {fmtMinutes(Math.abs(delta))} vs. Vorwoche
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end gap-6">
        {/* Lernserie */}
        <div className="flex items-center gap-4 sm:pr-6 sm:border-r sm:border-cream-200 flex-shrink-0">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center ${data.current_streak > 0 ? 'bg-terra/10' : 'bg-cream-100'}`}>
            <Flame className={`w-7 h-7 ${data.current_streak > 0 ? 'text-terra' : 'text-espresso/30'}`} strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-espresso/45 font-bold">Lernserie</div>
            <div className="font-display text-4xl text-espresso leading-none mt-1" data-testid="streak-days">
              {data.current_streak} <span className="text-xl">{data.current_streak === 1 ? 'Tag' : 'Tage'}</span>
            </div>
            <div className="text-xs text-espresso/50 mt-1">
              {data.longest_streak > data.current_streak
                ? `Rekord: ${data.longest_streak} Tage`
                : data.current_streak > 0
                  ? 'Dein Rekord — weiter so!'
                  : 'Heute lernen und Serie starten'}
            </div>
          </div>
        </div>

        {/* Lernzeit-Balken Mo–So */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.2em] text-espresso/45 font-bold">
              Lernzeit · Ziel: {data.weekly_goal_days} Lerntage
            </div>
            {data.goal_reached ? (
              <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: GOLD_INK }} data-testid="streak-goal-reached">
                <CheckCircle2 className="w-3.5 h-3.5" /> Wochenziel erreicht!
              </span>
            ) : (
              <span className="text-xs text-espresso/50" data-testid="streak-goal-remaining">
                Noch {remaining} {remaining === 1 ? 'Lerntag' : 'Lerntage'} bis zum Ziel
              </span>
            )}
          </div>

          <div className="flex items-end gap-2 sm:gap-3" style={{ height: 76 }}>
            {(days.length ? days : Array.from({ length: 7 }, (_, i) => ({ label: ['Mo','Di','Mi','Do','Fr','Sa','So'][i], minutes: 0, is_today: false, is_future: false }))).map((d, i) => {
              const active = data.week_active?.[i];
              const barH = d.minutes > 0 ? Math.max(6, Math.round((d.minutes / maxMin) * 48)) : 0;
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1.5 min-w-0" data-testid={`week-bar-${i}`}>
                  {d.minutes > 0 && (
                    <span className="text-[9px] font-semibold" style={{ color: d.is_today ? TERRA : '#8A8175' }}>{d.minutes}′</span>
                  )}
                  {d.minutes > 0 ? (
                    <div
                      className="w-full max-w-[26px] rounded-t-[4px] transition-all duration-500"
                      style={{ height: barH, backgroundColor: d.is_today ? TERRA : GOLD, opacity: d.is_today ? 1 : 0.85 }}
                    />
                  ) : active ? (
                    <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(216,146,50,0.2)' }} title="Gelernt (Zeit ab jetzt erfasst)">
                      <Check className="w-3 h-3" style={{ color: GOLD_INK }} strokeWidth={3} />
                    </div>
                  ) : (
                    <div className="w-full max-w-[26px] rounded-t-[4px]" style={{ height: 3, backgroundColor: d.is_future ? '#EDE6DC' : '#E0D6CA' }} />
                  )}
                  <span className={`text-[10px] uppercase tracking-wider font-semibold ${d.is_today ? 'text-terra' : 'text-espresso/40'}`}>
                    {d.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Motivations-Zeile */}
      <div className="mt-5 pt-4 border-t border-cream-200">
        <p className="font-display italic text-[14.5px]" style={{ color: TERRA }} data-testid="week-motivation">
          ✳ {motivation}
        </p>
      </div>
    </div>
  );
};

export default StreakCard;
